<script setup>
  import { ref, computed, watchEffect, watch } from 'vue';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useSpotStore } from "@/stores/SpotStore";
  import { useUserStore } from "@/stores/UserStore";
  import SegmentedControl from "@/components/controls/SegmentedControl.vue";
  import SegmentedControlButton from "@/components/controls/SegmentedControlButton.vue";
  import ToggleList from '@/components/controls/ToggleList.vue';
  import ToggleItem from '@/components/controls/ToggleItem.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import useContainerConfigurationGlobals from '@/composables/useContainerConfigurationGlobals';
  import ControlButton from '@/components/controls/ControlButton.vue';
  import Popup from '@/components/controls/Popup.vue';
  import Checkbox from '@/components/controls/Checkbox.vue';
  import Icon from '@/components/icons/Icon.vue';

  const { $WEB_VIEW } = useSettingsGlobals();
  const { $WIND_GRAPH } = useContainerConfigurationGlobals();

  const emit = defineEmits(['basicGraphZoomLevel:change', 'graphType:change', 'dataField:change', 'nearbySpots:change', 'nearcastWind:change']);

  const props = defineProps({
    showNCWindToggle: {
      type: Boolean,
      default: false
    },
    ncWindDataEnabled: {
      type: Boolean,
      required: false
    },
    windGraphType: {
      type: String,
      default: 'basic',
    },
    modelsSelected: {
      type: Array,
      required: false
    },  
    dataField: {
      type: String,
      required: false
    },
    spotId: {
      type: Number,
      required: false
    },
    zoom: {
      type: Number,
      required: false,
      default: 12
    }
  })

  const STORE_ID = props.spotId || null;

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const spotStore = useSpotStore(STORE_ID);
  const userStore = useUserStore();

  const graphType = ref();
  const zoomLevel = ref(12);
  const field = ref('wind');
  const nearbySpots = ref(null);
  const nearbySpotsLoading = ref(true);
  const selectedNearbySpots = ref([]);
  const selectedModelIds = ref(props.modelsSelected || []);
  const ncWindDataEnabled = ref(props.ncWindDataEnabled || false);

  const showBasicGraphZoomControl = computed(() => graphType.value === 'basic');
  const showDetailedGraphZoomControl = computed(() => graphType.value === 'detailed');
  const showDataFieldsMenu = computed(() => $WIND_GRAPH.show_data_fields_menu && graphType.value === 'detailed');
  const showNearbySpotsMenu = computed(() => $WIND_GRAPH.show_nearby_spots && graphType.value === 'detailed');
  const showNearbySpotsMenuDisabled = computed(() => nearbySpots.value?.length === 0 ? true : null);
  const nearbySpotsMenuLabel = computed(() => {
    if (nearbySpotsLoading.value) {
      return 'Loading...';
    }

    if (nearbySpots.value?.length === 0) {
      return 'No Nearby Spots';
    }

    return `Nearby Spots <span class="jwx-dropdown-control__label-badge">${nearbySpots.value.length}</span>`;
  });

  function handleBasicGraphZoomLevelChange(event) {
    emit('basicGraphZoomLevel:change', parseInt(event.value));
  }

  function handleNearcastWindChange(event) {
    emit('nearcastWind:change', event);
  }

  function handleBasicGraphTypeChange(event) {
    graphType.value = event.value;
    emit('graphType:change', event.value);
  }

  function handledataFieldChange(event) {
    const selectedCapability = event.target.value;
    emit('dataField:change', selectedCapability);
  }

  function handleNearbySpotsChange() {
    emit('nearbySpots:change', [spotStore.spot, ...selectedNearbySpots.value]);

    // If there are not selected nearby spots, send null to reset the graph to single station mode
    if (selectedNearbySpots.value.length === 0) {
       window.$EventBus.dispatchEvent('multi-station-list-change', { stationList: null, spotList: `${spotStore.spot.spot_id}`, colors: null });
    }
  }

  function handleDetailedGraphZoomIn() {
    window.$EventBus.dispatchEvent('wind-graph-detailed-zoom-in', {});
  }

  function handleDetailedGraphZoomOut() {
    window.$EventBus.dispatchEvent('wind-graph-detailed-zoom-out', {});
  }

  watchEffect(() => field.value = props.dataField  || 'wind');
  watchEffect(() => selectedModelIds.value = props.modelsSelected || []);
  watchEffect(() => {
    // Check if prop zoom is set if not
    // Check for zoom level of 11 which the graph code used to set as a default and causes no visible selected zoom level in the control
    // If so, set to 12 the default instead

    zoomLevel.value = 
      !props.zoom 
        ? 12 
        : props.zoom && props.zoom === 11 
        ? 12 
        : props.zoom
  });

  watch(selectedModelIds, (newSelectedModelIds) => {
    if (newSelectedModelIds.length === 0 && graphType.value === 'basic' && zoomLevel.value === 24) {
      zoomLevel.value = 12;

      emit('basicGraphZoomLevel:change', 12);
    }
  });

  watch(() => props.windGraphType, (newType) => {
    graphType.value = newType;
  }, { immediate: true });  

  watchEffect(async () => {
    if (showNearbySpotsMenu.value) {
      nearbySpotsLoading.value = true;
      const data = await spotStore.getNearbySpots(`${spotStore.lat}:${spotStore.lon}`, null, true);
      data.spots = data.spots.filter(spot => spot.spot_id !== spotStore.spot.spot_id); // Remove current spot 

      nearbySpots.value = data.spots;
      nearbySpotsLoading.value = false;
    };
  });

  watchEffect(() => ncWindDataEnabled.value = props.ncWindDataEnabled || false);
</script>

<template>
  <div
    class="jwx-control-bar"
  >
    <div class="jwx-control-bar__controls-left">
      <SegmentedControl
        v-if="showBasicGraphZoomControl"
        @segmentChanged="handleBasicGraphZoomLevelChange"
        v-model.number="zoomLevel"
        label="Select the time period length to display on the graph."
        style="min-width: 160px; --segmented-control-background-color: var(--jwx-control-background-color-level-1)"
      >
        <SegmentedControlButton :value="4">4h</SegmentedControlButton>
        <SegmentedControlButton :value="12">12h</SegmentedControlButton>
        <SegmentedControlButton :value="24" :disabled="selectedModelIds.length === 0">24h</SegmentedControlButton>
      </SegmentedControl>

      <ul v-if="showDetailedGraphZoomControl" class="jwx-button-control-group">
        <li class="jwx-button-control-group__item">
          <ControlButton
            @click.prevent="handleDetailedGraphZoomOut"
            data-action="zoom-out"
          >
            <Icon name="minus" class="jwx-button-control__icon" /> 
          </ControlButton>
        </li>        
        <li class="jwx-button-control-group__item">
          <ControlButton
            @click.prevent="handleDetailedGraphZoomIn"
          >
            <Icon name="plus" class="jwx-button-control__icon" /> 
          </ControlButton>
        </li>       
      </ul>

      <div
        v-if="showDataFieldsMenu"
        class="jwx-select-control"
        data-border="false"
        data-font-size="small"
      >
        <select
          :id="`detail-panel-data-field-${spotStore.spotId}`"
          @change="handledataFieldChange"
          data-border="false"
          v-model="field"
        >
          <option 
            v-for="{displayName, value} in spotStore.stationCapabilities" 
            :key="value" 
            :value="value"
          >
            {{ displayName }}
          </option>
        </select>

        <Icon name="chevron-up-down" class="jwx-select-control__action-icon" />
      </div>

      <Popup
        v-if="showNearbySpotsMenu"
        :id="`detail-panel-popup-menu-${spotStore.spotId}`"
        positionY="bottom"
        positionX="center"
        :disabled="showNearbySpotsMenuDisabled"
      >
        <template #button-content>
          <div
            class="jwx-dropdown-control"
            type="button"
          >
            <p class="jwx-dropdown-control__label" v-html="nearbySpotsMenuLabel"> </p>
            <Icon name="chevron-up-down" class="jwx-dropdown-control__action-icon" :data-disabled="showNearbySpotsMenuDisabled" />
          </div>
        </template>
        <template #menu-content>
          <div
          class="jwx-multiselect-menu"
          style="min-width: 7.8125rem; max-height: 50vh;"
          >
            <ul class="jwx-multiselect-menu__list">
              <li
                v-for="spot in nearbySpots"
                :key="spot.spot_id"
                class="jwx-multiselect-menu__option"
              >
                <Checkbox
                  @change.prevent="handleNearbySpotsChange"
                  :id="`nearby-station-${spot.spot_id}`"
                  :name="`nearby-station-${spot.spot_id}`"
                  :checked="selectedNearbySpots.find(nearbySpot => nearbySpot.spot_id === spot.spot_id) !== undefined"
                  :value="spot"
                  labelClassList="jwx-multiselect-menu__option-label"
                  v-model="selectedNearbySpots"
                >
                    <div class="jwx-multiselect-menu__label-name">{{ spot.name }}</div>
                    <div class="jwx-multiselect-menu__label-meta">{{ spot.distance }} {{ userStore.units.distance }}</div>
                </Checkbox>
              </li>
            </ul>
          </div>
        </template>
      </Popup>  
    </div>
    <div class="jwx-control-bar__controls-right">
      <div v-if="props.showNCWindToggle" class="jwx-data-container__control-group jwx-data-container__control-group--toggle">
        <ToggleList>
          <ToggleItem
            id="show-nc-wind"
            @toggle:changed="handleNearcastWindChange"
            :smallText="true"
            v-model="ncWindDataEnabled"
          >
            Nearcast&trade; Wind
          </ToggleItem>
        </ToggleList>
      </div>

      <SegmentedControl
        v-if="isDesktop && !$WEB_VIEW"
        @segmentChanged="handleBasicGraphTypeChange"
        v-model="graphType"
        label="Choose to display the Basic or Detailed Graph."
        style="min-width: 160px; margin-left: auto; --segmented-control-background-color: var(--jwx-control-background-color-level-1);"
      >
        <SegmentedControlButton value="basic">Basic</SegmentedControlButton>
        <SegmentedControlButton value="detailed">Detailed</SegmentedControlButton>
      </SegmentedControl>
    </div>
  </div>
</template>